/**
 * Variant Availability Checker
 * Centralized service that uses Storefront API as the source of truth
 * for variant availability across the PDP (delivery options, stock bar, variant swatches)
 */
(function() {
  'use strict';

  const STOREFRONT_TOKEN = 'f03bcbe287205c23fd45fdd8609eefb1';
  
  // Cache for availability data
  const availabilityCache = new Map();
  
  // In-flight requests to prevent duplicate concurrent API calls
  const inFlightRequests = new Map();
  
  // Subscribers that want to be notified of availability changes
  const subscribers = new Set();

  /**
   * Convert numeric variant ID to GraphQL GID
   */
  function toVariantGid(numericId) {
    return `gid://shopify/ProductVariant/${numericId}`;
  }

  /**
   * Normalize location name for comparison
   */
  function normalizeLocationName(name) {
    if (!name) return '';
    const normalized = name.toLowerCase().trim();
    if (normalized.includes('hartville')) return 'hartville';
    if (normalized.includes('middlefield')) return 'middlefield';
    if (normalized.includes('shop') && !normalized.includes('hartville') && !normalized.includes('middlefield')) {
      return 'shop';
    }
    return normalized;
  }

  function normalizeStoreKey(value) {
    if (!value) return '';
    const normalized = String(value).toLowerCase().trim();
    if (normalized.includes('hartville')) return 'hartville';
    if (normalized.includes('middlefield')) return 'middlefield';
    if (
      normalized.includes('shop') ||
      normalized.includes('online') ||
      normalized.includes('warehouse') ||
      normalized.includes('shipping') ||
      normalized.includes('ecom') ||
      normalized.includes('e-commerce') ||
      normalized.includes('main store') ||
      normalized.includes('main')
    ) {
      return 'shop';
    }
    return normalized;
  }

  function getAssignedStoreKey() {
    const isSupportedKey = (key) => key === 'hartville' || key === 'middlefield' || key === 'shop';

    try {
      const stored = localStorage.getItem('geo_assigned_store');
      if (stored) {
        const parsed = JSON.parse(stored);
        const fromKey = normalizeStoreKey(parsed?.key);
        if (isSupportedKey(fromKey)) return fromKey;
        const fromName = normalizeStoreKey(parsed?.name || parsed?.title || parsed?.store);
        if (isSupportedKey(fromName)) return fromName;
      }
    } catch (e) {
      // ignore and continue
    }

    if (window.geoLocationService && typeof window.geoLocationService.getAssignedStore === 'function') {
      try {
        const assignedStore = window.geoLocationService.getAssignedStore();
        const fromService = normalizeStoreKey(assignedStore?.name || assignedStore?.key);
        if (isSupportedKey(fromService)) return fromService;
      } catch (e) {
        // ignore
      }
    }

    return '';
  }

  function getExplicitAssignedStoreKey() {
    try {
      const stored = localStorage.getItem('geo_assigned_store');
      if (!stored) return '';
      const parsed = JSON.parse(stored);
      const key = normalizeStoreKey(parsed?.key);
      if (key === 'hartville' || key === 'middlefield' || key === 'shop') return key;
    } catch (e) {
      // ignore
    }
    return '';
  }

  function hasActiveGeoLocation() {
    try {
      const overrideStr = localStorage.getItem('geo_location_override');
      if (overrideStr) {
        const override = JSON.parse(overrideStr);
        if (override?.lat && override?.lng) return true;
      }
    } catch (e) {
      // ignore
    }

    try {
      const locationStr = localStorage.getItem('geo_user_location');
      if (locationStr) {
        const location = JSON.parse(locationStr);
        if (location?.lat && location?.lng) return true;
      }
    } catch (e) {
      // ignore
    }

    if (window.geoLocationService && typeof window.geoLocationService.getCurrentLocation === 'function') {
      try {
        const location = window.geoLocationService.getCurrentLocation();
        if (location?.lat && location?.lng) return true;
      } catch (e) {
        // ignore
      }
    }

    return false;
  }

  /**
   * Check if user is within radius of a store
   */
  function isUserWithinStoreRadius(storeName) {
    if (!window.geoLocationService) return false;
    try {
      return window.geoLocationService.isWithinStoreRadius(storeName);
    } catch (e) {
      return false;
    }
  }

  /**
   * Fetch store availability from Storefront API
   */
  async function fetchStoreAvailability(variantGid, forceRefresh = false) {
    // Check cache first (unless force refresh)
    if (!forceRefresh && availabilityCache.has(variantGid)) {
      return availabilityCache.get(variantGid);
    }

    // Check if there's already an in-flight request for this variant
    // This prevents duplicate API calls when multiple components request the same data simultaneously
    if (inFlightRequests.has(variantGid)) {
      return inFlightRequests.get(variantGid);
    }

    const query = `
      query StoreAvailability($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            availableForSale
            quantityAvailable
            storeAvailability(first: 20) {
              nodes {
                location { id name }
                available
                quantityAvailable
                pickUpTime
              }
            }
          }
        }
      }
    `;

    // Create the request promise and store it
    const requestPromise = (async () => {
      try {
        const res = await fetch('/api/2025-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
          },
          body: JSON.stringify({
            query,
            variables: { variantId: variantGid }
          })
        });

        const json = await res.json();
        if (json.errors) throw new Error(JSON.stringify(json.errors));

        const variantData = json?.data?.node;
        const storeAvailability = variantData?.storeAvailability?.nodes || [];

        // Parse and structure the data
        const result = {
          variantId: variantGid,
          availableForSale: variantData?.availableForSale ?? false,
          totalQuantityAvailable: variantData?.quantityAvailable ?? 0,
          locations: {}
        };

        storeAvailability.forEach(store => {
          const normalized = normalizeLocationName(store.location.name);
          result.locations[normalized] = {
            name: store.location.name,
            available: store.available,
            quantity: store.quantityAvailable,
            pickUpTime: store.pickUpTime
          };
        });

        // Calculate effective availability based on user location
        result.effectiveAvailability = calculateEffectiveAvailability(result);

        // Cache the result
        availabilityCache.set(variantGid, result);

        return result;
      } catch (error) {
        console.error('Error fetching store availability:', error);
        return null;
      } finally {
        // Clean up in-flight request
        inFlightRequests.delete(variantGid);
      }
    })();

    // Store the promise for other concurrent callers
    inFlightRequests.set(variantGid, requestPromise);

    return requestPromise;
  }

  /**
   * Calculate effective availability based on user location
   * Returns the quantity that's actually available to the user
   */
  function calculateEffectiveAvailability(data) {
    const isWithinHartville = isUserWithinStoreRadius('hartville');
    const isWithinMiddlefield = isUserWithinStoreRadius('middlefield');
    const assignedStoreKey = getAssignedStoreKey();
    const explicitAssignedStoreKey = getExplicitAssignedStoreKey();
    const hasGeoLocation = hasActiveGeoLocation();
    const assignedIsPickupStore = assignedStoreKey === 'hartville' || assignedStoreKey === 'middlefield';
    const allowPickupByAssignment = hasGeoLocation && (!explicitAssignedStoreKey || assignedIsPickupStore);
    const canUseHartvillePickup = allowPickupByAssignment && (assignedStoreKey ? assignedStoreKey === 'hartville' : isWithinHartville);
    const canUseMiddlefieldPickup = allowPickupByAssignment && (assignedStoreKey ? assignedStoreKey === 'middlefield' : isWithinMiddlefield);

    const shopStock = data.locations.shop?.quantity ?? 0;
    const hartvilleStock = data.locations.hartville?.quantity ?? 0;
    const middlefieldStock = data.locations.middlefield?.quantity ?? 0;

    // Shipping is always available if shop has stock
    const hasShipping = shopStock > 0;

    // Pickup availability depends on user location
    const hasHartvillePickup = canUseHartvillePickup && hartvilleStock > 0;
    const hasMiddlefieldPickup = canUseMiddlefieldPickup && middlefieldStock > 0;

    // Determine the quantity to show (prioritize shipping, then local pickup)
    let quantity = 0;
    let source = null;

    if (hasShipping) {
      quantity = shopStock;
      source = 'shipping';
    } else if (hasHartvillePickup) {
      quantity = hartvilleStock;
      source = 'hartville';
    } else if (hasMiddlefieldPickup) {
      quantity = middlefieldStock;
      source = 'middlefield';
    }

    return {
      available: hasShipping || hasHartvillePickup || hasMiddlefieldPickup,
      quantity,
      source,
      hasShipping,
      hasHartvillePickup,
      hasMiddlefieldPickup,
      shippingQuantity: shopStock,
      hartvilleQuantity: hartvilleStock,
      middlefieldQuantity: middlefieldStock
    };
  }

  function getAudienceStoreKey() {
    const explicit = getExplicitAssignedStoreKey();
    if (explicit === 'hartville' || explicit === 'middlefield' || explicit === 'shop') return explicit;

    const assigned = getAssignedStoreKey();
    if (assigned === 'hartville' || assigned === 'middlefield' || assigned === 'shop') return assigned;

    if (isUserWithinStoreRadius('hartville')) return 'hartville';
    if (isUserWithinStoreRadius('middlefield')) return 'middlefield';
    return 'shop';
  }

  function getLocationStockValue(location) {
    if (!location) return 0;
    const quantity = Number(location.quantity);
    if (Number.isFinite(quantity) && quantity > 0) return quantity;
    return 0;
  }

  function getContextualStockSummary(data) {
    if (!data || !data.locations) return { available: false, quantity: 0 };
    const audienceStoreKey = getAudienceStoreKey();
    const shopQty = getLocationStockValue(data.locations.shop);
    const hartvilleQty = getLocationStockValue(data.locations.hartville);
    const middlefieldQty = getLocationStockValue(data.locations.middlefield);

    let quantity = shopQty;
    if (audienceStoreKey === 'hartville') quantity += hartvilleQty;
    if (audienceStoreKey === 'middlefield') quantity += middlefieldQty;

    return {
      available: quantity > 0,
      quantity
    };
  }

  async function getContextualAvailabilityMap(variantIds) {
    const ids = Array.from(new Set((variantIds || []).filter(Boolean).map((id) => String(id))));
    if (!ids.length) return new Map();

    const results = await Promise.all(
      ids.map(async (id) => {
        const data = await getVariantAvailability(id, false);
        const summary = getContextualStockSummary(data);
        if (typeof summary.available === 'boolean') return [id, summary];
        return [id, null];
      })
    );

    return new Map(results);
  }

  function getOptionPositionFromInputName(inputName) {
    const name = inputName || '';
    const parts = name.split('-');
    const pos = parseInt(parts[parts.length - 1], 10);
    return Number.isFinite(pos) ? pos : null;
  }

  function variantMatchesSelectionUpToOption(variant, selectedOptions, optionIndex, candidateValue) {
    for (let i = 0; i <= optionIndex; i += 1) {
      const expected = i === optionIndex ? candidateValue : selectedOptions[i];
      const variantValue = normalizeOptionValueForAvailability(variant[`option${i + 1}`]);
      if (variantValue !== normalizeOptionValueForAvailability(expected)) return false;
    }
    return true;
  }

  /**
   * Get availability for a variant (uses cache if available)
   */
  async function getVariantAvailability(variantId, forceRefresh = false) {
    const gid = typeof variantId === 'string' && variantId.includes('gid://') 
      ? variantId 
      : toVariantGid(variantId);
    return fetchStoreAvailability(gid, forceRefresh);
  }

  /**
   * Subscribe to availability updates
   */
  function subscribe(callback) {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  }

  /**
   * Notify all subscribers of availability data
   */
  function notifySubscribers(variantId, data) {
    subscribers.forEach(callback => {
      try {
        callback(variantId, data);
      } catch (e) {
        console.error('Error in availability subscriber:', e);
      }
    });
  }

  /**
   * Refresh availability and notify subscribers
   */
  async function refreshAndNotify(variantId, forceRefresh = true) {
    const data = await getVariantAvailability(variantId, forceRefresh);
    if (data) {
      notifySubscribers(variantId, data);
      
      // Dispatch custom event for components that prefer event-based updates
      document.dispatchEvent(new CustomEvent('availability:updated', {
        detail: { variantId, data },
        bubbles: true
      }));
    }
    return data;
  }

  /**
   * Clear cache (useful when location changes)
   */
  function clearCache() {
    availabilityCache.clear();
  }

  /**
   * Clear X (unavailable) state only from *color* swatches. Size/fit keep their Liquid-rendered
   * state (crossed out if out of stock). Used on initial load so default is: no X on colors, X on size/fit when out of stock.
   */
  function clearAllOptionUnavailableState() {
    clearColorOptionUnavailableState();
  }

  /** True if this input belongs to the Color option (e.g. name "Color-1"). */
  function isColorOptionInput(input) {
    const name = (input.getAttribute && input.getAttribute('name')) || input.name || '';
    const optionName = name.replace(/-?\d+$/, '').trim();
    return optionName.toLowerCase().indexOf('color') !== -1;
  }

  /** Clear X only from color swatch inputs inside the given variant-selects (or first one if not passed). */
  function clearColorOptionUnavailableState(variantSelects) {
    const root = variantSelects || document.querySelector('variant-selects');
    if (!root) return;
    root.querySelectorAll('input[type="radio"][name][data-option-value-id]').forEach((input) => {
      if (!isColorOptionInput(input)) return;
      input.classList.remove('disabled', 'visually-disabled');
      const label = root.getRootNode().querySelector(`label[for="${input.id}"]`);
      if (label) label.classList.remove('visually-disabled');
    });
  }

  function normalizeOptionValueForAvailability(val) {
    if (val == null || val === '') return '';
    const s = String(val).trim();
    return s.includes('|') ? s.split('|')[0].trim() : s;
  }

  /**
   * Update all *color* swatches to show X when that color (with current size/fit) is unavailable or doesn't exist.
   * So X is visible before the user clicks (on load and when variant changes).
   * Scoped to the section's variant-selects so we don't touch other product forms (e.g. quick add).
   */
  async function updateActiveOptionAvailability(variantId, sectionId) {
    const variants = window.productVariants;
    if (!variants || !variants.length) return;

    const variant = variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    const productInfo = sectionId
      ? document.querySelector(`#MainProduct-${sectionId}`) || document.querySelector(`product-info[data-section="${sectionId}"]`)
      : null;
    const variantSelects = productInfo ? productInfo.querySelector('variant-selects') : document.querySelector('variant-selects');
    if (!variantSelects) return;

    const root = variantSelects.getRootNode();
    const currentOptions = [variant.option1, variant.option2, variant.option3].filter((o) => o != null);

    // Clear color swatches' X so we can re-apply from current variant state (e.g. after section re-render)
    clearColorOptionUnavailableState(variantSelects);

    // For each option value, determine if any variant beneath that branch is in stock for
    // the shopper context (shop + assigned pickup store where applicable).
    const colorInputs = Array.from(variantSelects.querySelectorAll('input[type="radio"][name][data-option-value-id]')).filter(isColorOptionInput);
    const colorOptionPosition = getOptionPositionFromInputName(colorInputs[0]?.getAttribute('name'));
    const colorOptionIndex = colorOptionPosition ? colorOptionPosition - 1 : 0;
    if (isNaN(colorOptionIndex) || colorOptionIndex < 0) return;

    const variantIdsToCheck = [];
    const optionsToEvaluate = colorInputs.map((input) => {
      const candidateValue = input.value;
      const matchingVariants = variants.filter((candidate) =>
        variantMatchesSelectionUpToOption(candidate, currentOptions, colorOptionIndex, candidateValue)
      );
      matchingVariants.forEach((candidate) => {
        if (candidate?.id) variantIdsToCheck.push(candidate.id);
      });
      return { input, matchingVariants, optionIndex: colorOptionIndex };
    });

    const contextualAvailability = await getContextualAvailabilityMap(variantIdsToCheck);

    optionsToEvaluate.forEach(({ input, matchingVariants, optionIndex }) => {
      const label = root.querySelector(`label[for="${input.id}"]`);
      const hasAnyVariant = matchingVariants.length > 0;
      const hasAnyInStock = matchingVariants.some((candidate) => {
        const contextual = contextualAvailability.get(String(candidate.id));
        return Boolean(contextual?.available);
      });
      const showUnavailable = !hasAnyVariant || !hasAnyInStock;
      const isBottomLevel = optionIndex === currentOptions.length - 1;
      input.disabled = isBottomLevel && showUnavailable;
      if (showUnavailable) {
        input.classList.add('disabled');
        input.classList.add('visually-disabled');
        if (label) label.classList.add('visually-disabled');
      } else {
        input.classList.remove('disabled');
        input.classList.remove('visually-disabled');
        if (label) label.classList.remove('visually-disabled');
      }
    });
  }

  /**
   * For size/fit (non-color) options:
   * - Non-existing variant: disable input so user cannot click; show X (visually-disabled).
   * - Existing but out of stock: keep clickable; show X (visually-disabled).
   * - Existing and in stock: clickable; no X.
   */
  async function updateNonColorOptionsVariantExistence(variantId, sectionId) {
    const variants = window.productVariants;
    if (!variants || !variants.length) return;

    const variant = variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    const currentOptions = [variant.option1, variant.option2, variant.option3].filter((o) => o != null);
    const productInfo = sectionId
      ? document.querySelector(`#MainProduct-${sectionId}`) || document.querySelector(`product-info[data-section="${sectionId}"]`)
      : null;
    const variantSelects = productInfo ? productInfo.querySelector('variant-selects') : document.querySelector('variant-selects');
    if (!variantSelects) return;

    const root = variantSelects.getRootNode();

    function normalizeOptionValue(val) {
      if (val == null || val === '') return '';
      const s = String(val).trim();
      return s.includes('|') ? s.split('|')[0].trim() : s;
    }

    function findVariant(optionValues) {
      return variants.find((v) => {
        const o1 = normalizeOptionValue(v.option1);
        const o2 = normalizeOptionValue(v.option2);
        const o3 = normalizeOptionValue(v.option3);
        const a1 = normalizeOptionValue(optionValues[0]);
        const a2 = normalizeOptionValue(optionValues[1]);
        const a3 = normalizeOptionValue(optionValues[2]);
        return o1 === a1 && o2 === a2 && o3 === a3;
      });
    }

    const nonColorInputs = Array.from(variantSelects.querySelectorAll('input[type="radio"][name][data-option-value-id]')).filter((input) => !isColorOptionInput(input));
    const variantIdsToCheck = [];
    const optionsToEvaluate = [];

    nonColorInputs.forEach((input) => {
      if (isColorOptionInput(input)) return;
      const pos = getOptionPositionFromInputName(input.getAttribute('name'));
      if (isNaN(pos) || pos < 1 || pos > currentOptions.length) return;
      const optionIndex = pos - 1;
      const candidateValue = input.value;
      const matchingVariants = variants.filter((candidate) =>
        variantMatchesSelectionUpToOption(candidate, currentOptions, optionIndex, candidateValue)
      );
      matchingVariants.forEach((candidate) => {
        if (candidate?.id) variantIdsToCheck.push(candidate.id);
      });
      optionsToEvaluate.push({ input, matchingVariants, optionIndex });
    });

    const contextualAvailability = await getContextualAvailabilityMap(variantIdsToCheck);

    optionsToEvaluate.forEach(({ input, matchingVariants, optionIndex }) => {
      const label = root.querySelector(`label[for="${input.id}"]`);
      if (!matchingVariants.length) {
        // Variant doesn't exist: show X and disable bottom-level values
        const isBottomLevel = optionIndex === currentOptions.length - 1;
        input.disabled = isBottomLevel;
        input.classList.add('disabled', 'visually-disabled');
        if (label) label.classList.add('visually-disabled');
        return;
      }

      // Variant exists: always clickable
      input.disabled = false;
      input.classList.remove('disabled');
      const isAvailable = matchingVariants.some((candidate) => {
        const contextual = contextualAvailability.get(String(candidate.id));
        return Boolean(contextual?.available);
      });
      const isBottomLevel = optionIndex === currentOptions.length - 1;
      if (isAvailable) {
        input.classList.remove('visually-disabled');
        if (label) label.classList.remove('visually-disabled');
      } else {
        // Out of stock for shopper context: show X (disable bottom-level values)
        input.disabled = isBottomLevel;
        if (isBottomLevel) input.classList.add('disabled');
        input.classList.add('visually-disabled');
        if (label) label.classList.add('visually-disabled');
      }
    });

    variantSelects.querySelectorAll('select.select__select').forEach((select) => {
      const name = select.getAttribute('name') || '';
      const parts = name.split('-');
      const pos = parseInt(parts[parts.length - 1], 10);
      if (isNaN(pos) || pos < 1 || pos > currentOptions.length) return;
      const optionIndex = pos - 1;
      select.querySelectorAll('option').forEach((opt) => {
        const hypothetical = currentOptions.slice();
        hypothetical[optionIndex] = opt.value;
        const found = findVariant(hypothetical);
        opt.disabled = !found;
      });
    });
  }

  /**
   * Update variant swatch availability based on API data
   */
  function updateVariantSwatchAvailability(variantId, data) {
    if (!data || !data.effectiveAvailability) return;

    const isAvailable = data.effectiveAvailability.available;

    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      const submitButtons = productInfo.querySelectorAll('[type="submit"][data-sold-out-message]');
      submitButtons.forEach(btn => {
        if (isAvailable) {
          btn.removeAttribute('disabled');
        } else {
          btn.setAttribute('disabled', 'disabled');
        }
      });

      const quantityInputs = productInfo.querySelectorAll('quantity-input');
      quantityInputs.forEach(qi => {
        if (isAvailable) {
          qi.classList.remove('quantity-disabled');
          qi.querySelector('.quantity__input')?.removeAttribute('disabled');
        } else {
          qi.classList.add('quantity-disabled');
          qi.querySelector('.quantity__input')?.setAttribute('disabled', 'disabled');
        }
      });
    }
  }

  /**
   * Update stock bar with API data
   */
  function updateStockBar(variantId, data) {
    if (!data || !data.effectiveAvailability) return;

    const stockBarContainer = document.querySelector('[id^="stock-bar-container-"]');
    if (!stockBarContainer) return;

    const quantity = data.effectiveAvailability.quantity;
    stockBarContainer.setAttribute('data-initial-stock', quantity);

    // Dispatch event to trigger stock bar re-render
    document.dispatchEvent(new CustomEvent('delivery-option-selected', {
      detail: { quantity }
    }));
  }

  // Update only the active *color* swatch so X shows only on it when out of stock. Also disable size/fit
  // options that have no variant so the user cannot click them. Defer so we run after section DOM has finished updating.
  async function refreshOptionAvailabilityUi(variantId, sectionId) {
    await updateActiveOptionAvailability(variantId, sectionId);
    await updateNonColorOptionsVariantExistence(variantId, sectionId);
  }

  document.addEventListener('variant:changed', (e) => {
    const variantId = e.detail?.variantId;
    const sectionId = e.detail?.sectionId;
    if (variantId) {
      requestAnimationFrame(() => {
        refreshOptionAvailabilityUi(variantId, sectionId);
      });
    }
  });

  document.addEventListener('variant:changed', (e) => {
    const variantId = e.detail?.variantId;
    if (!variantId) return;
    refreshAndNotify(variantId, true).then((data) => {
      if (data) {
        updateVariantSwatchAvailability(variantId, data);
        updateStockBar(variantId, data);
      }
    });
  });

  // Listen for geo-location changes
  function getCurrentPdpVariantContext() {
    const productInfo = document.querySelector('product-info');
    const sectionId = productInfo?.dataset?.section || null;

    if (productInfo) {
      const scopedSelectors = [
        'input[name="id"].product-variant-id',
        'form[data-type="add-to-cart-form"] input[name="id"]',
        sectionId ? `#product-form-${sectionId} input[name="id"]` : null,
        sectionId ? `#product-form-installment-${sectionId} input[name="id"]` : null
      ].filter(Boolean);

      for (const selector of scopedSelectors) {
        const input = productInfo.querySelector(selector);
        if (input?.value) return { variantId: input.value, sectionId };
      }
    }

    const fallbackInput = document.querySelector('input[name="id"].product-variant-id, form[data-type="add-to-cart-form"] input[name="id"]');
    return {
      variantId: fallbackInput?.value || null,
      sectionId
    };
  }

  document.addEventListener('geo:location-updated', () => {
    clearCache();
    // Re-fetch availability for current variant
    const { variantId, sectionId } = getCurrentPdpVariantContext();
    if (variantId) {
      refreshAndNotify(variantId, true).then(() => {
        requestAnimationFrame(() => {
          refreshOptionAvailabilityUi(variantId, sectionId);
        });
      });
    }
  });

  // Legacy compatibility: some geo flows still emit this event name.
  document.addEventListener('geo-location-updated', () => {
    clearCache();
    const { variantId, sectionId } = getCurrentPdpVariantContext();
    if (variantId) {
      refreshAndNotify(variantId, true).then(() => {
        requestAnimationFrame(() => {
          refreshOptionAvailabilityUi(variantId, sectionId);
        });
      });
    }
  });

  // Initial fetch on page load
  function initialFetch() {
    const { variantId } = getCurrentPdpVariantContext();
    if (variantId) {
      refreshAndNotify(variantId, false);
    }
  }

  // Run initial fetch, then set color swatch X's and size/fit state from current variant
  function onReady() {
    setTimeout(() => {
      initialFetch();
      clearAllOptionUnavailableState();
      const { variantId, sectionId } = getCurrentPdpVariantContext();
      if (variantId) {
        requestAnimationFrame(() => {
          refreshOptionAvailabilityUi(variantId, sectionId);
        });
      }
    }, 100);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Expose the service globally
  window.variantAvailabilityChecker = {
    getVariantAvailability,
    refreshAndNotify,
    subscribe,
    clearCache,
    toVariantGid,
    isUserWithinStoreRadius,
    normalizeLocationName
  };

})();